import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from '@mui/material';
import { useNavigate } from "react-router";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import { SearchInput, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getAllPayslips } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { money, periodLabel } from "../payrollUtils";

const nameOf = (p) => p.employee ? `${p.employee.fname || ""} ${p.employee.lname || ""}`.trim() : "—";

const AllPayslips = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllPayslips();
      if (res?.success === false) throw new Error(res?.message || "Failed to load payslips");
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load payslips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((p) => nameOf(p).toLowerCase().includes(q)) : data;
  }, [data, search]);

  return (
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Payslips</Box>
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee" minWidth="16rem" />
      </Stack>

      <DataTable
        rows={filtered}
        loading={loading}
        error={error}
        onRetry={fetchData}
        errorTitle="Couldn't load payslips"
        empty={{ title: "No payslips yet", description: "Approve a payroll run to generate payslips." }}
        onRowClick={(p) => navigate(`/app/settings/payroll/payslip/${p._id}`)}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          { key: "employee", label: "Employee", width: "28%", cellSx: { fontWeight: 600 }, render: (p) => nameOf(p) },
          { key: "period", label: "Period", cellSx: { color: tokens.textMuted }, render: (p) => periodLabel(p.month, p.year) },
          { key: "gross", label: "Gross", align: "right", render: (p) => money(p.grossPay) },
          { key: "deductions", label: "Deductions", align: "right", cellSx: { color: tokens.danger }, render: (p) => money(p.totalDeductions) },
          { key: "net", label: "Net Pay", align: "right", cellSx: { fontWeight: 600 }, render: (p) => money(p.netPay) },
        ]}
      />
    </MainBox>
  );
};

export default AllPayslips;
